<template>
    <AddFileSection
        :id="AddFileFormSectionIds.REQUEST_IDENTITY_VERIFICATION"
        sectionDescription="Select clients to complete identity verification for this file."
        sectionHeading="Request identity verification"
    >
        <ClientMultiSelect
            v-model="selectedClients"
            class="mt-3"
            :clientOptions
            :error="requestIDVRecipientsError"
            :required="false"
        />
    </AddFileSection>
</template>

<script setup lang="ts">
import { ClientSelectionErrorKeys } from '@/company-portal/add-file/add-file-form-errors.types';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import type { AddFileClient } from '@/company-portal/add-file/add-file.types';
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import { useAddFileError } from '@/company-portal/add-file/use-add-file-error';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import ClientMultiSelect from '@/components/Views/Company/AddFile/ClientMultiSelect.vue';
import type { ClientOption, ClientType } from '@/components/Views/Company/File/file.types';
import { joinFirstLast } from '@/shared/utils/strings';
import { useFileClientStore } from '@/stores/company/file-client.store';
import { useUserStore } from '@/stores/company/user.store';
import type { Role } from '@/types/closinglock.types';
import { computed, ref, watch } from 'vue';

const addFileStore = useAddFileStore();
const fileClientStore = useFileClientStore();
const userStore = useUserStore();
const { serverValidationErrors, clearServerValidationError } = useAddFileError();

const selectedClients = ref<ClientOption[]>([]);

const clientOptions = computed<ClientOption[]>(() =>
    fileClientStore.availableClientOptions.map(client => ({
        id: client.id,
        value: client.id,
        email: client.email,
        first: client.first,
        last: client.last,
        label: joinFirstLast(client.first, client.last),
        phone: client.phone,
        /** @todo cleanup in [CLENG-883] assertion is needed because we have duplicate type declarations for client role  */
        role: client.role as ClientType,
        // IDV-dependent clients are auto-selected and disabled
        disabled: !!(client.role && userStore.idvDependentClientTypes.includes(client.role as Role)),
    })),
);

const idvRequiredClients = computed(() =>
    clientOptions.value.filter(option => userStore.requireIdvClientTypes.includes(option.role as Role)),
);

// IDV-required clients are auto-selected so they appear checked and show chips
const applyAutoSelection = (toAdd: ClientOption[], toRemove: Map<AddFileClient['id'], ClientOption>) => {
    // Remove from selectedClients any client that was required but is no longer
    const current = selectedClients.value.filter(client => !toRemove.has(client.id));
    const currentMap = new Map(current.map(client => [client.id, client]));
    // Add any newly required clients that are not yet selected
    selectedClients.value = [...current, ...toAdd.filter(client => !currentMap.has(client.id))];
};

watch(
    idvRequiredClients,
    (newIdvRequiredClients, oldIdvRequiredClients) => {
        const newIdvRequiredMap = new Map(newIdvRequiredClients.map(client => [client.id, client]));
        const oldIdvRequiredMap = new Map(
            (oldIdvRequiredClients ?? []).filter(client => !newIdvRequiredMap.has(client.id)).map(client => [client.id, client]),
        );
        applyAutoSelection(newIdvRequiredClients, oldIdvRequiredMap);
    },
    { immediate: true },
);

watch(
    selectedClients,
    newSelectedClients => {
        addFileStore.requestIdvClientIds = newSelectedClients.map(client => client.id);
        clearServerValidationError(ClientSelectionErrorKeys.REQUEST_ID_VERIFICATION_RECIPIENTS);
    },
    { immediate: true },
);

const requestIDVRecipientsError = computed(() =>
    serverValidationErrors.value?.[ClientSelectionErrorKeys.REQUEST_ID_VERIFICATION_RECIPIENTS]?.join(' '),
);
</script>
